/**
 * Schedule API Response Transformer
 * 
 * Transforms backend schedule responses to match UI expectations.
 * Handles field name mismatches and value transformations.
 */

import { Schedule, ScheduleStatus, ScheduleFrequencyType } from '../types';
import cronParser from 'cron-parser';
import { convertEventBridgeToStandardCron } from '../hooks/useCronValidation';

/**
 * Backend schedule response format
 */
interface BackendSchedule {
  scheduleId: string;
  jobId: string;
  userId?: string;
  scheduleName: string;
  description?: string;
  cronExpression?: string;
  scheduleExpression?: string;
  timezone?: string;
  status?: string; // Direct status from Java service: ENABLED/DISABLED
  state?: string; // EventBridge state: ENABLED/DISABLED
  scheduleStatus?: string; // DynamoDB status: ACTIVE/AUTO_DISABLED (from Python Lambda path)
  schedulerArn?: string;
  eventBridgeArn?: string;
  nextExecutionTime?: string;
  lastExecutionTime?: string;
  lastExecutionStatus?: string;
  executionCount?: number;
  consecutiveFailures?: number;
  lastFailureReason?: string;
  lastFailureAt?: string;
  autoDisabledAt?: string;
  autoDisableReason?: string;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
}

/**
 * Transform backend schedule status to UI status
 */
function transformStatus(status?: string, scheduleStatus?: string, state?: string): ScheduleStatus {
  // Priority: scheduleStatus (DynamoDB via Lambda) > status (Java service) > state (EventBridge)
  if (status === 'AUTO_DISABLED' || scheduleStatus === 'AUTO_DISABLED') {
    return 'AUTO_DISABLED';
  }
  
  if (status === 'ENABLED' || state === 'ENABLED' || scheduleStatus === 'ACTIVE') {
    return 'ENABLED';
  }
  
  if (status === 'DISABLED' || state === 'DISABLED' || scheduleStatus === 'PAUSED') {
    return 'DISABLED';
  }
  
  return 'DISABLED';
}

/**
 * Infer frequency type from schedule expression
 */
function inferFrequencyType(expression?: string): ScheduleFrequencyType {
  if (!expression) return 'CRON';
  
  if (expression.startsWith('rate(')) {
    return 'RATE';
  }
  
  return 'CRON';
}

/**
 * Compute the next execution time from a cron expression.
 * Returns ISO string or undefined if parsing fails.
 */
function computeNextExecutionTime(scheduleExpression: string, timezone?: string): string | undefined {
  try {
    // Extract raw cron from cron(...) wrapper
    const match = scheduleExpression.match(/^cron\((.+)\)$/);
    const rawCron = match ? match[1] : scheduleExpression;

    // Convert EventBridge 6-field cron to standard 5-field
    const standardCron = convertEventBridgeToStandardCron(rawCron);
    if (!standardCron) return undefined;

    const interval = cronParser.parseExpression(standardCron, {
      tz: timezone || 'UTC',
      currentDate: new Date(),
    });
    const next = interval.next();
    return next.toDate().toISOString();
  } catch {
    return undefined;
  }
}

/**
 * Transform backend schedule to UI schedule format
 */
export function transformSchedule(backendSchedule: BackendSchedule): Schedule {
  // Determine schedule expression (prefer cronExpression, fallback to scheduleExpression)
  const rawExpression = backendSchedule.cronExpression || backendSchedule.scheduleExpression || '';
  
  // Wrap in cron() if not already wrapped
  const scheduleExpression = rawExpression.startsWith('cron(') || rawExpression.startsWith('rate(')
    ? rawExpression
    : `cron(${rawExpression})`;
  
  // Always compute nextExecutionTime from cron expression for accuracy.
  // The backend value can be stale (not updated after cron changes or executions).
  const nextExecutionTime = computeNextExecutionTime(scheduleExpression, backendSchedule.timezone)
    || backendSchedule.nextExecutionTime;

  return {
    scheduleId: backendSchedule.scheduleId,
    jobId: backendSchedule.jobId,
    scheduleName: backendSchedule.scheduleName,
    description: backendSchedule.description,
    scheduleExpression,
    frequencyType: inferFrequencyType(scheduleExpression),
    timezone: backendSchedule.timezone || 'UTC',
    status: transformStatus(backendSchedule.status, backendSchedule.scheduleStatus, backendSchedule.state),
    schedulerArn: backendSchedule.schedulerArn || backendSchedule.eventBridgeArn,
    nextExecutionTime,
    lastExecutionTime: backendSchedule.lastExecutionTime,
    lastExecutionStatus: backendSchedule.lastExecutionStatus,
    executionCount: backendSchedule.executionCount,
    consecutiveFailures: backendSchedule.consecutiveFailures,
    lastFailureReason: backendSchedule.lastFailureReason,
    lastFailureAt: backendSchedule.lastFailureAt,
    autoDisabledAt: backendSchedule.autoDisabledAt,
    autoDisableReason: backendSchedule.autoDisableReason,
    createdAt: backendSchedule.createdAt,
    updatedAt: backendSchedule.updatedAt,
    createdBy: backendSchedule.createdBy || backendSchedule.userId || 'unknown',
  };
}

/**
 * Transform array of backend schedules
 */
export function transformSchedules(backendSchedules: BackendSchedule[]): Schedule[] {
  return backendSchedules.map(transformSchedule);
}
